"use strict";
/*
Copyright 2020 Splunk Inc.

Licensed under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License.
You may obtain a copy of the License at

http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/
Object.defineProperty(exports, "__esModule", { value: true });
exports.SplunkUserInteractionInstrumentation = exports.DEFAULT_AUTO_INSTRUMENTED_EVENT_NAMES = exports.DEFAULT_AUTO_INSTRUMENTED_EVENTS = void 0;
const api_1 = require("@opentelemetry/api");
const core_1 = require("@opentelemetry/core");
const instrumentation_user_interaction_1 = require("@opentelemetry/instrumentation-user-interaction");
exports.DEFAULT_AUTO_INSTRUMENTED_EVENTS = {
    // pointer
    click: true,
    dblclick: true,
    mousedown: true,
    mouseup: true,
    // form
    submit: true,
    reset: true,
    change: true,
    // drap & drop
    dragend: true,
    drop: true,
    // media
    ended: true,
    pause: true,
    play: true,
};
exports.DEFAULT_AUTO_INSTRUMENTED_EVENT_NAMES = Object.keys(exports.DEFAULT_AUTO_INSTRUMENTED_EVENTS);
const ROUTING_INSTRUMENTATION_NAME = 'route';
const ROUTING_INSTRUMENTATION_VERSION = '1';
class SplunkUserInteractionInstrumentation extends instrumentation_user_interaction_1.UserInteractionInstrumentation {
    constructor(config = {}) {
        // Prefer otel's eventNames property
        if (!config.eventNames) {
            const eventMap = Object.assign({}, exports.DEFAULT_AUTO_INSTRUMENTED_EVENTS, config.events);
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const eventNames = Object.entries(eventMap).filter(([_eventName, enabled]) => enabled).map(([eventName]) => eventName);
            config.eventNames = eventNames;
        }
        super(config);
        this._routingTracer = api_1.trace.getTracer(ROUTING_INSTRUMENTATION_NAME, ROUTING_INSTRUMENTATION_VERSION);
        const _superCreateSpan = this._createSpan.bind(this);
        this._createSpan = (element, eventName, parentSpan) => {
            // Fix: No span is created when event is captured from document
            if (element === document) {
                element = document.documentElement;
            }
            return _superCreateSpan(element, eventName, parentSpan);
        };
        const _superPatchAddEventListener = this._patchAddEventListener.bind(this);
        this._patchAddEventListener = () => {
            const patcher = _superPatchAddEventListener();
            return (original) => {
                const patchedListener = patcher(original);
                // Fix: Error when .addEventListener(type, listener, null)
                return function (type, listener, useCapture) {
                    if (useCapture === null) {
                        useCapture = undefined;
                    }
                    return patchedListener.call(this, type, listener, useCapture);
                };
            };
        };
    }
    setTracerProvider(tracerProvider) {
        super.setTracerProvider(tracerProvider);
        this._routingTracer = tracerProvider.getTracer(ROUTING_INSTRUMENTATION_NAME, ROUTING_INSTRUMENTATION_VERSION);
    }
    getZoneWithPrototype() {
        // FIXME work out ngZone issues with Angular  PENDING
        return undefined;
    }
    enable() {
        this.__hashChangeHandler = (event) => {
            this._emitRouteChangeSpan(event.oldURL);
        };
        // Hash can be changed with location.hash = '#newThing', no way to hook that directly...
        window.addEventListener('hashchange', this.__hashChangeHandler);
        super.enable();
    }
    disable() {
        super.disable();
        window.removeEventListener('hashchange', this.__hashChangeHandler);
    }
    // FIXME find cleaner way to patch
    _patchHistoryMethod() {
        const plugin = this;
        return (original) => {
            return function patchHistoryMethod(...args) {
                const oldHref = location.href;
                const result = original.apply(this, args);
                const newHref = location.href;
                if (oldHref !== newHref) {
                    plugin._emitRouteChangeSpan(oldHref);
                }
                return result;
            };
        };
    }
    _emitRouteChangeSpan(oldHref) {
        const now = (0, core_1.hrTime)();
        const span = this._routingTracer.startSpan('routeChange', { startTime: now });
        span.setAttribute('component', this.moduleName);
        span.setAttribute('prev.href', oldHref);
        // location.href set with new value by default
        span.end(now);
    }
}
exports.SplunkUserInteractionInstrumentation = SplunkUserInteractionInstrumentation;
//# sourceMappingURL=SplunkUserInteractionInstrumentation.js.map